import { RefObject, useEffect, useRef, useState } from 'react';

export interface UseStickyOnScrollOptions {
  /**
   * Offset from the top of the viewport (in px) at which the element should be
   * considered "sticky".
   *
   * Example: if you have a fixed header of 64px, set offsetTop: 64.
   *
   * @default 0
   */
  offsetTop?: number;
  /**
   * Tolerance (in px) to avoid flicker around the threshold.
   *
   * @default 0.5
   */
  tolerance?: number;
  /**
   * Whether the hook is enabled.
   *
   * @default true
   */
  enabled?: boolean;
}

export interface UseStickyOnScrollResult {
  /**
   * Attach this to a zero-height element placed immediately before the element
   * you want to become sticky (the "trigger").
   */
  sentinelRef: RefObject<HTMLDivElement | null>;
  /**
   * True once the sentinel has reached the top (offsetTop) during scroll.
   */
  isSticky: boolean;
}

/**
 * Custom hook to detect when an element has reached the top of the viewport on scroll.
 *
 * How it works:
 * - Render a "sentinel" element right before your target element.
 * - Once the sentinel's top is <= offsetTop, `isSticky` becomes true.
 *
 * This is robust even if the target element switches to `position: sticky/fixed`,
 * because the sentinel remains in normal document flow.
 *
 * @example
 * const { sentinelRef, isSticky } = useStickyOnScroll();
 * return (
 *   <>
 *     <div ref={sentinelRef} className="h-0" aria-hidden />
 *     <nav className={isSticky ? 'nav-sticky' : ''}>...</nav>
 *   </>
 * );
 */
export function useStickyOnScroll(options: UseStickyOnScrollOptions = {}): UseStickyOnScrollResult {
  const { offsetTop = 0, tolerance = 0.5, enabled = true } = options;

  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    let rafId: number | null = null;

    const update = () => {
      rafId = null;
      const el = sentinelRef.current;
      if (!el) return;

      const top = el.getBoundingClientRect().top;
      const next = top <= offsetTop + tolerance;
      setIsSticky((prev) => (prev === next ? prev : next));
    };

    const onScrollOrResize = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [enabled, offsetTop, tolerance]);

  return { sentinelRef, isSticky };
}

